import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CockpitShell from "../../components/CockpitShell";
import config from "../../config/config";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../components/ui/dialog";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: "🧠" },
  { href: "/dashboard/admin/assessment-ops", label: "Assessment", icon: "🧭" },
  { href: "/dashboard/admin/wissenstest", label: "Wissenstest", icon: "🧪" },
  { href: "/dashboard/admin/wissenstest-management", label: "WT Mgmt", icon: "🗂️" },
  { href: "/dashboard/admin/users", label: "Users", icon: "👥" },
  { href: "/dashboard/systemstatus", label: "System", icon: "📡" },
];

const statusTone = {
  queued: "bg-amber-500/15 text-amber-100 ring-1 ring-amber-300/30",
  running: "bg-cyan-500/15 text-cyan-100 ring-1 ring-cyan-300/30",
  review_required: "bg-fuchsia-500/15 text-fuchsia-100 ring-1 ring-fuchsia-300/30",
  completed: "bg-emerald-500/15 text-emerald-100 ring-1 ring-emerald-300/30",
  failed: "bg-rose-500/15 text-rose-100 ring-1 ring-rose-300/30",
  archived: "bg-slate-500/15 text-slate-100 ring-1 ring-slate-300/30",
  published: "bg-emerald-500/15 text-emerald-100 ring-1 ring-emerald-300/30",
  approved: "bg-cyan-500/15 text-cyan-100 ring-1 ring-cyan-300/30",
  review_failed: "bg-rose-500/15 text-rose-100 ring-1 ring-rose-300/30",
};

const verdictTone = {
  pass: "bg-emerald-500/15 text-emerald-100 ring-1 ring-emerald-300/30",
  manual_required: "bg-amber-500/15 text-amber-100 ring-1 ring-amber-300/30",
  reject: "bg-rose-500/15 text-rose-100 ring-1 ring-rose-300/30",
};

const moduleLabels = {
  professional: "Berufswissen",
  language: "Sprachpruefung",
  civics: "Deutschlandwissen",
};

const scopeLabels = {
  global: "Global",
  family: "Familie",
  group: "Gruppe",
  profession: "Beruf",
};

function formatStatus(value) {
  return String(value || "-").replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("de-DE");
}

function canPublishQuestion(status) {
  return status === "approved";
}

function canArchiveQuestion(status) {
  return ["draft", "approved", "published", "review_failed"].includes(status);
}

function buildQuestionActionHint(status) {
  if (status === "review_failed") return "Diese Frage kann nur archiviert werden. Publish ist fuer review_failed gesperrt.";
  if (status === "published") return "Diese Frage ist bereits veroeffentlicht und kann nur archiviert werden.";
  if (status === "archived") return "Archivierte Fragen koennen hier nicht weiter freigegeben werden.";
  if (status === "draft") return "Draft-Fragen muessen erst den Review-Pfad durchlaufen, bevor sie publiziert werden koennen.";
  return "Publish ist nur aus approved erlaubt.";
}

function formatReviewValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
}

function MetaField({ label, value, tone = "text-slate-200" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/55 p-3">
      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className={`mt-2 break-words text-sm ${tone}`}>{value || "-"}</div>
    </div>
  );
}

function SidebarSection({ title, subtitle, children }) {
  return (
    <section className="min-w-0 rounded-3xl border border-white/10 bg-slate-950/55 p-5">
      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{title}</div>
      {subtitle ? <p className="mt-2 text-xs leading-5 text-slate-400">{subtitle}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function AnswerOptionCard({ answer }) {
  const isCorrect = Number(answer.is_correct) === 1;
  return (
    <div
      className={`overflow-hidden rounded-[28px] border ${isCorrect ? "border-emerald-300/30 bg-emerald-500/10 shadow-[0_0_0_1px_rgba(16,185,129,0.12)]" : "border-white/10 bg-slate-900/75"}`}
    >
      <div className={`flex items-center justify-between gap-4 border-b px-5 py-4 ${isCorrect ? "border-emerald-300/20 bg-emerald-400/10" : "border-white/10 bg-white/[0.03]"}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl text-sm font-semibold ${isCorrect ? "bg-emerald-300/15 text-emerald-100" : "bg-white/10 text-slate-200"}`}>
            {answer.option_key}
          </div>
          <div>
            <div className="text-sm font-semibold text-white">Antwortoption {answer.option_key}</div>
            <div className="text-xs text-slate-400">{isCorrect ? "Als korrekt markiert" : "Als falsch markiert"}</div>
          </div>
        </div>
        <Badge className={isCorrect ? "bg-emerald-500/20 px-3 py-1 text-emerald-50 ring-1 ring-emerald-300/30" : "bg-white/10 px-3 py-1 text-slate-200 ring-1 ring-white/10"}>
          {isCorrect ? "Korrekte Antwort" : "Falsche Antwort"}
        </Badge>
      </div>
      <div className="px-5 py-5">
        <p className="whitespace-pre-wrap text-base leading-7 text-slate-100">{answer.answer_text}</p>
      </div>
    </div>
  );
}

export default function AssessmentOpsAdmin() {
  const navigate = useNavigate();
  const selectContentClass = "border-white/15 bg-slate-950/98 text-slate-100 shadow-2xl";
  const fieldLabelClass = "text-xs font-semibold uppercase tracking-[0.22em] text-slate-400";
  const fieldHelpClass = "text-xs text-slate-500";

  const [overview, setOverview] = useState({ jobs: [], questions: [], latest_jobs: [], published_by_module: [] });
  const [jobs, setJobs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [professionRows, setProfessionRows] = useState([]);
  const [professionHits, setProfessionHits] = useState([]);
  const [professionSearch, setProfessionSearch] = useState("");
  const [jobStatus, setJobStatus] = useState("all");
  const [jobModule, setJobModule] = useState("all");
  const [jobLoading, setJobLoading] = useState(false);
  const [jobCreating, setJobCreating] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionActionBusy, setQuestionActionBusy] = useState("");
  const [questionDetailOpen, setQuestionDetailOpen] = useState(false);
  const [questionDetailLoading, setQuestionDetailLoading] = useState(false);
  const [questionDetailError, setQuestionDetailError] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedQuestionDetail, setSelectedQuestionDetail] = useState(null);
  const [professionLoading, setProfessionLoading] = useState(false);
  const [jobForm, setJobForm] = useState({
    module_type: "professional",
    requested_question_count: 24,
    priority: 180,
    max_repair_attempts: 1,
    beruf_id: "",
    beruf_label: "",
  });

  const publishedMap = useMemo(() => {
    const out = new Map();
    (overview.published_by_module || []).forEach((entry) => out.set(entry.module_type, Number(entry.total || 0)));
    return out;
  }, [overview]);

  function handleApiError(err, fallback) {
    const status = err?.response?.status;
    const message = err?.response?.data?.message || fallback;
    if (status === 401) {
      navigate("/login");
      return;
    }
    window.alert(message);
  }

  async function loadOverview() {
    const res = await axios.get(`${config.API_BASE_URL}/api/admin/assessment/overview`, { headers: authHeaders() });
    setOverview(res.data || { jobs: [], questions: [], latest_jobs: [], published_by_module: [] });
  }

  async function loadJobs() {
    setJobLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/assessment/jobs`, {
        params: {
          page: 1,
          limit: 20,
          status: jobStatus === "all" ? undefined : jobStatus,
          moduleType: jobModule === "all" ? undefined : jobModule,
        },
        headers: authHeaders(),
      });
      setJobs(res.data?.rows || []);
    } finally {
      setJobLoading(false);
    }
  }

  async function loadQuestions() {
    setQuestionLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/assessment/questions`, {
        params: { page: 1, limit: 20 },
        headers: authHeaders(),
      });
      setQuestions(res.data?.rows || []);
    } finally {
      setQuestionLoading(false);
    }
  }

  async function loadProfessionMappings() {
    setProfessionLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/assessment/professions`, {
        params: { page: 1, limit: 20, q: professionSearch || undefined },
        headers: authHeaders(),
      });
      setProfessionRows(res.data?.rows || []);
    } finally {
      setProfessionLoading(false);
    }
  }

  async function loadQuestionDetail(questionId) {
    setQuestionDetailLoading(true);
    setQuestionDetailError("");
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/assessment/questions/${questionId}`, {
        headers: authHeaders(),
      });
      setSelectedQuestionDetail(res.data || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate("/login");
        return;
      }
      setSelectedQuestionDetail(null);
      setQuestionDetailError(err?.response?.data?.message || "Frage-Details konnten nicht geladen werden.");
    } finally {
      setQuestionDetailLoading(false);
    }
  }

  useEffect(() => {
    document.title = "Experts4Europe | Assessment Ops";
    Promise.all([loadOverview(), loadJobs(), loadQuestions(), loadProfessionMappings()]).catch(() => {});
  }, []);

  useEffect(() => {
    loadJobs().catch(() => {});
  }, [jobStatus, jobModule]);

  useEffect(() => {
    const term = professionSearch.trim();
    if (term.length < 2) {
      setProfessionHits([]);
      loadProfessionMappings().catch(() => {});
      return;
    }
    const timer = setTimeout(() => {
      axios.get(`${config.API_BASE_URL}/api/admin/assessment/professions/search`, {
        params: { q: term, limit: 12 },
        headers: authHeaders(),
      }).then((res) => setProfessionHits(res.data?.rows || [])).catch(() => setProfessionHits([]));
      loadProfessionMappings().catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [professionSearch]);

  async function createJob() {
    try {
      if (jobForm.module_type === "professional" && !jobForm.beruf_id) {
        window.alert("Bitte zuerst einen Beruf waehlen.");
        return;
      }
      setJobCreating(true);
      await axios.post(`${config.API_BASE_URL}/api/admin/assessment/jobs`, {
        module_type: jobForm.module_type,
        beruf_id: jobForm.module_type === "professional" ? Number(jobForm.beruf_id) : undefined,
        requested_question_count: Number(jobForm.requested_question_count || 24),
        priority: Number(jobForm.priority || 180),
        max_repair_attempts: Number(jobForm.max_repair_attempts || 1),
      }, { headers: authHeaders() });
      await Promise.all([loadOverview(), loadJobs(), loadProfessionMappings()]);
    } catch (err) {
      handleApiError(err, "Job konnte nicht angelegt werden.");
    } finally {
      setJobCreating(false);
    }
  }

  async function runJobNow(jobId) {
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/assessment/jobs/${jobId}/run`, {}, { headers: authHeaders() });
      await Promise.all([loadOverview(), loadJobs(), loadQuestions()]);
    } catch (err) {
      handleApiError(err, "Job konnte nicht gestartet werden.");
    }
  }

  async function runNextQueued() {
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/assessment/jobs/run-next`, {}, { headers: authHeaders() });
      await Promise.all([loadOverview(), loadJobs(), loadQuestions()]);
    } catch (err) {
      handleApiError(err, "Queue konnte nicht verarbeitet werden.");
    }
  }

  async function openQuestionDetail(questionId) {
    setSelectedQuestionId(questionId);
    setQuestionDetailOpen(true);
    await loadQuestionDetail(questionId);
  }

  async function openAdjacentQuestion(direction) {
    if (!selectedQuestionId || questionDetailLoading || questionActionBusy) return;
    const currentIndex = questions.findIndex((entry) => Number(entry.id) === Number(selectedQuestionId));
    if (currentIndex < 0) return;
    const nextEntry = questions[currentIndex + direction];
    if (!nextEntry) return;
    await openQuestionDetail(nextEntry.id);
  }

  async function performQuestionAction(questionId, action) {
    const actionKey = `${action}:${questionId}`;
    setQuestionActionBusy(actionKey);
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/admin/assessment/questions/${questionId}/${action}`,
        {},
        { headers: authHeaders() }
      );
      const row = res.data?.row || null;
      if (row) {
        setQuestions((prev) => prev.map((entry) => (entry.id === questionId ? { ...entry, ...row } : entry)));
        setSelectedQuestionDetail((prev) => {
          if (!prev || Number(prev.id) !== Number(questionId)) return prev;
          return {
            ...prev,
            ...row,
            reviews: prev.reviews || [],
            review_flags_json: row.review_flags_json ?? prev.review_flags_json,
            beruf_label: row.beruf_label || prev.beruf_label,
          };
        });
      }
      await Promise.all([loadOverview(), loadQuestions()]);
      if (questionDetailOpen && Number(selectedQuestionId) === Number(questionId)) {
        await loadQuestionDetail(questionId);
      }
    } catch (err) {
      handleApiError(err, action === "publish" ? "Frage konnte nicht veroeffentlicht werden." : "Frage konnte nicht archiviert werden.");
    } finally {
      setQuestionActionBusy("");
    }
  }

  async function publishQuestion(questionId) {
    await performQuestionAction(questionId, "publish");
  }

  async function archiveQuestion(questionId) {
    await performQuestionAction(questionId, "archive");
  }

  async function savePriority(berufId, priorityScore) {
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/assessment/professions/${berufId}/priority`, {
        priority_score: Number(priorityScore || 100),
      }, { headers: authHeaders() });
      await loadProfessionMappings();
    } catch (err) {
      handleApiError(err, "Prioritaet konnte nicht gespeichert werden.");
    }
  }

  const detailReviewCount = selectedQuestionDetail?.reviews?.length || 0;
  const detailAnswers = Array.isArray(selectedQuestionDetail?.answers) ? selectedQuestionDetail.answers : [];
  const detailFlags = Array.isArray(selectedQuestionDetail?.review_flags_json) ? selectedQuestionDetail.review_flags_json : [];
  const publishDisabled = !canPublishQuestion(selectedQuestionDetail?.status) || questionActionBusy !== "";
  const archiveDisabled = !canArchiveQuestion(selectedQuestionDetail?.status) || questionActionBusy !== "";
  const selectedQuestionIndex = questions.findIndex((entry) => Number(entry.id) === Number(selectedQuestionId));
  const hasPreviousQuestion = selectedQuestionIndex > 0;
  const hasNextQuestion = selectedQuestionIndex >= 0 && selectedQuestionIndex < questions.length - 1;
  const reviewWorkspaceTitle = selectedQuestionDetail ? `Review Frage #${selectedQuestionDetail.id}` : "Review Frage";

  return (
    <>
      <CockpitShell
        title="Assessment Operations"
        subtitle="Queue, Blueprint-Generierung, Review-Freigaben und Priorisierung fuer Sprach-, Deutschland- und Berufswissen."
        badge="Assessment Engine"
        navItems={navItems}
        wideLayout
        actions={
          <>
            <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={runNextQueued}>
              Run Next Queue
            </Button>
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => Promise.all([loadOverview(), loadJobs(), loadQuestions(), loadProfessionMappings()])}>
              Refresh
            </Button>
          </>
        }
      >
        <div className="flex w-full flex-col gap-4">
          <div className="grid gap-4 lg:grid-cols-4">
            <Card className="card-e4e">
              <CardHeader><CardTitle className="font-display text-white">Published Language</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-white">{publishedMap.get("language") || 0}</p></CardContent>
            </Card>
            <Card className="card-e4e">
              <CardHeader><CardTitle className="font-display text-white">Published Civics</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-white">{publishedMap.get("civics") || 0}</p></CardContent>
            </Card>
            <Card className="card-e4e">
              <CardHeader><CardTitle className="font-display text-white">Published Professional</CardTitle></CardHeader>
              <CardContent><p className="text-3xl font-bold text-white">{publishedMap.get("professional") || 0}</p></CardContent>
            </Card>
            <Card className="card-e4e">
              <CardHeader><CardTitle className="font-display text-white">Latest Queue State</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(overview.jobs || []).map((entry) => (
                  <div key={entry.status} className="flex items-center justify-between text-sm text-slate-200">
                    <span>{formatStatus(entry.status)}</span>
                    <span>{entry.total}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card className="card-e4e">
            <CardHeader>
              <CardTitle className="font-display text-white">Generation Job anlegen</CardTitle>
            </CardHeader>
            <CardContent className="grid items-start gap-4 overflow-visible md:grid-cols-2 xl:grid-cols-[180px_minmax(360px,2.7fr)_120px_120px_170px_auto]">
              <div className="space-y-2">
                <label className={fieldLabelClass}>Modul / Testbereich</label>
                <Select value={jobForm.module_type} onValueChange={(value) => {
                  setJobForm((prev) => ({
                    ...prev,
                    module_type: value,
                    beruf_id: value === "professional" ? prev.beruf_id : "",
                    beruf_label: value === "professional" ? prev.beruf_label : "",
                  }));
                  if (value !== "professional") setProfessionHits([]);
                }}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white">
                    <SelectValue placeholder="Modul / Testbereich waehlen" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="professional">Berufswissen</SelectItem>
                    <SelectItem value="language">Sprachpruefung</SelectItem>
                    <SelectItem value="civics">Deutschlandwissen</SelectItem>
                  </SelectContent>
                </Select>
                <p className={fieldHelpClass}>Welcher Fragenpool durch diesen Job befuellt werden soll.</p>
              </div>

              <div className="relative min-w-0 space-y-2 xl:min-w-[360px]">
                <label className={fieldLabelClass}>Beruf</label>
                <Input
                  value={jobForm.beruf_label}
                  onChange={(e) => {
                    setJobForm((prev) => ({ ...prev, beruf_label: e.target.value, beruf_id: "" }));
                    setProfessionSearch(e.target.value);
                  }}
                  placeholder={jobForm.module_type === "professional" ? "z. B. Elektronikerin / Elektroniker" : "Nur fuer Berufswissen erforderlich"}
                  disabled={jobForm.module_type !== "professional"}
                  className="border-white/20 bg-slate-950/40 text-white"
                />
                {jobForm.module_type === "professional" && professionHits.length > 0 ? (
                  <div className="absolute left-0 top-[calc(100%+0.45rem)] z-30 min-w-[360px] max-w-[520px] overflow-hidden rounded-xl border border-white/10 bg-slate-950/95 shadow-2xl backdrop-blur">
                    {professionHits.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        className="block w-full border-b border-white/5 px-3 py-3 text-left text-sm leading-6 text-slate-200 last:border-b-0 hover:bg-white/10"
                        onClick={() => {
                          setJobForm((prev) => ({ ...prev, beruf_id: String(entry.id), beruf_label: entry.beruf }));
                          setProfessionHits([]);
                        }}
                      >
                        {entry.beruf}
                      </button>
                    ))}
                  </div>
                ) : null}
                <p className={fieldHelpClass}>
                  {jobForm.module_type === "professional"
                    ? "Nur fuer Modul Berufswissen. Der gewaehlte Beruf definiert Scope, Risiko und Zielpool."
                    : "Bei Sprach- und Deutschlandwissen wird kein einzelner Beruf benoetigt."}
                </p>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>Zielanzahl Fragen</label>
                <Input
                  type="number"
                  min={3}
                  max={120}
                  value={jobForm.requested_question_count}
                  onChange={(e) => setJobForm((prev) => ({ ...prev, requested_question_count: Number(e.target.value || 24) }))}
                  className="border-white/20 bg-slate-950/40 text-white"
                  placeholder="z. B. 24"
                />
                <p className={fieldHelpClass}>Wie viele Fragen dieser Job fuer den Pool erzeugen soll. Aktueller Standard: 24.</p>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>Prioritaet</label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={jobForm.priority}
                  onChange={(e) => setJobForm((prev) => ({ ...prev, priority: Number(e.target.value || 180) }))}
                  className="border-white/20 bg-slate-950/40 text-white"
                  placeholder="z. B. 180"
                />
                <p className={fieldHelpClass}>Hoeher = frueher verarbeitet. Aktueller Standard: 180.</p>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>Reparaturversuche</label>
                <Input
                  type="number"
                  min={0}
                  max={3}
                  value={jobForm.max_repair_attempts}
                  onChange={(e) => setJobForm((prev) => ({ ...prev, max_repair_attempts: Number(e.target.value || 1) }))}
                  className="border-white/20 bg-slate-950/40 text-white"
                  placeholder="z. B. 1"
                />
                <p className={fieldHelpClass}>Wie oft eine ungueltige Frage vor dem Verwerfen automatisch korrigiert werden darf.</p>
              </div>

              <div className="flex items-end">
                <Button className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-300 xl:w-auto" onClick={createJob} disabled={jobCreating}>
                  {jobCreating ? "Lege Job an..." : "Queue Job"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="jobs" className="w-full">
            <TabsList className="bg-white/5">
              <TabsTrigger value="jobs">Jobs</TabsTrigger>
              <TabsTrigger value="questions">Questions</TabsTrigger>
              <TabsTrigger value="professions">Professions</TabsTrigger>
            </TabsList>
            <TabsContent value="jobs">
              <Card className="card-e4e">
                <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <CardTitle className="font-display text-white">Generation Queue</CardTitle>
                  <div className="grid gap-2 md:grid-cols-2">
                    <Select value={jobStatus} onValueChange={setJobStatus}>
                      <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>
                        <SelectItem value="all">Alle Status</SelectItem>
                        <SelectItem value="queued">Queued</SelectItem>
                        <SelectItem value="running">Running</SelectItem>
                        <SelectItem value="review_required">Review required</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={jobModule} onValueChange={setJobModule}>
                      <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Modul" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>
                        <SelectItem value="all">Alle Module</SelectItem>
                        <SelectItem value="language">Language</SelectItem>
                        <SelectItem value="civics">Civics</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto rounded-2xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-900/80 text-left text-slate-300">
                        <tr>
                          <th className="px-3 py-2">Job</th>
                          <th className="px-3 py-2">Scope</th>
                          <th className="px-3 py-2">Risk</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Questions</th>
                          <th className="px-3 py-2">Error</th>
                          <th className="px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.map((job) => (
                          <tr key={job.id} className="border-t border-white/10 text-slate-200">
                            <td className="px-3 py-2">
                              <div className="font-semibold text-white">#{job.id}</div>
                              <div className="text-xs text-slate-400">{job.module_type}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div>{job.beruf_label || job.group_name || "global"}</div>
                              <div className="text-xs text-slate-400">{job.scope_type}</div>
                            </td>
                            <td className="px-3 py-2">{job.risk_class}</td>
                            <td className="px-3 py-2">
                              <Badge className={statusTone[job.status] || statusTone.archived}>{job.status}</Badge>
                            </td>
                            <td className="px-3 py-2">{job.question_count}</td>
                            <td className="max-w-[360px] px-3 py-2 text-xs text-slate-400">{job.last_error_message || "-"}</td>
                            <td className="px-3 py-2">
                              <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100" disabled={job.status === "running" || jobLoading} onClick={() => runJobNow(job.id)}>
                                Run now
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {jobs.length === 0 ? (
                          <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">{jobLoading ? "Lade..." : "Keine Jobs vorhanden."}</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="questions">
              <Card className="card-e4e">
                <CardHeader>
                  <CardTitle className="font-display text-white">Question Pool</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto rounded-2xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-900/80 text-left text-slate-300">
                        <tr>
                          <th className="px-3 py-2">Question</th>
                          <th className="px-3 py-2">Module</th>
                          <th className="px-3 py-2">Scope</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Reviews</th>
                          <th className="px-3 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questions.map((row) => {
                          const isSelected = Number(selectedQuestionId) === Number(row.id);
                          const rowPublishDisabled = !canPublishQuestion(row.status) || questionActionBusy !== "";
                          const rowArchiveDisabled = !canArchiveQuestion(row.status) || questionActionBusy !== "";
                          return (
                            <tr
                              key={row.id}
                              className={`border-t border-white/10 text-slate-200 transition ${isSelected ? "bg-cyan-500/10" : "hover:bg-white/5"} cursor-pointer`}
                              onClick={() => openQuestionDetail(row.id)}
                            >
                              <td className="max-w-[620px] px-3 py-3">
                                <button
                                  type="button"
                                  className="text-left"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openQuestionDetail(row.id);
                                  }}
                                >
                                  <div className="line-clamp-2 text-white underline-offset-4 hover:underline">{row.question_text}</div>
                                  <div className="mt-1 text-xs text-slate-400">{row.beruf_label || row.category_key || "ohne Kategorie"}</div>
                                </button>
                              </td>
                              <td className="px-3 py-3">{moduleLabels[row.module_type] || row.module_type}</td>
                              <td className="px-3 py-3">{scopeLabels[row.scope_type] || row.scope_type}</td>
                              <td className="px-3 py-3"><Badge className={statusTone[row.status] || statusTone.archived}>{formatStatus(row.status)}</Badge></td>
                              <td className="px-3 py-3">{row.review_count}</td>
                              <td className="px-3 py-3">
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-white/20 bg-transparent text-slate-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openQuestionDetail(row.id);
                                    }}
                                  >
                                    Details
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-white/20 bg-transparent text-slate-100"
                                    disabled={rowPublishDisabled}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      publishQuestion(row.id);
                                    }}
                                  >
                                    Publish
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-rose-300/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                                    disabled={rowArchiveDisabled}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      archiveQuestion(row.id);
                                    }}
                                  >
                                    Archive
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {questions.length === 0 ? (
                          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">{questionLoading ? "Lade..." : "Keine Assessment-Fragen vorhanden."}</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Zeile, Fragetext oder Details-Button oeffnen die vollstaendige Review-Ansicht.</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="professions">
              <Card className="card-e4e">
                <CardHeader>
                  <CardTitle className="font-display text-white">Profession Mapping & Priorisierung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input value={professionSearch} onChange={(e) => setProfessionSearch(e.target.value)} placeholder="Beruf, Familie oder Gruppe suchen" className="border-white/20 bg-slate-950/40 text-white" />
                  <div className="overflow-x-auto rounded-2xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-900/80 text-left text-slate-300">
                        <tr>
                          <th className="px-3 py-2">Beruf</th>
                          <th className="px-3 py-2">Familie</th>
                          <th className="px-3 py-2">Gruppe</th>
                          <th className="px-3 py-2">Risk</th>
                          <th className="px-3 py-2">Priority</th>
                          <th className="px-3 py-2">Save</th>
                        </tr>
                      </thead>
                      <tbody>
                        {professionRows.map((row) => (
                          <tr key={row.id} className="border-t border-white/10 text-slate-200">
                            <td className="px-3 py-2">{row.beruf_label}</td>
                            <td className="px-3 py-2">{row.family_name}</td>
                            <td className="px-3 py-2">{row.group_name}</td>
                            <td className="px-3 py-2">{row.risk_class}</td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                defaultValue={row.priority_score}
                                className="w-28 border-white/20 bg-slate-950/40 text-white"
                                onBlur={(e) => savePriority(row.beruf_id, e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-400">Blur saves</td>
                          </tr>
                        ))}
                        {professionRows.length === 0 ? (
                          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">{professionLoading ? "Lade..." : "Noch keine Berufszuordnungen vorhanden."}</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </CockpitShell>

      <Dialog
        open={questionDetailOpen}
        onOpenChange={(open) => {
          setQuestionDetailOpen(open);
          if (!open) {
            setSelectedQuestionDetail(null);
            setQuestionDetailError("");
            setQuestionDetailLoading(false);
            setSelectedQuestionId(null);
          }
        }}
      >
        <DialogContent fullScreen className="flex h-[100dvh] w-screen items-center justify-center bg-black/72 p-4 text-slate-100 sm:p-5 xl:p-8">
          <div className="flex h-[92vh] w-full max-w-[1760px] flex-col overflow-hidden rounded-[30px] border border-white/15 bg-slate-950/97 shadow-[0_40px_120px_rgba(2,6,23,0.55)]">
            <div className="border-b border-white/10 bg-slate-950/96 px-6 py-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="min-w-0">
                  <DialogTitle className="font-display text-3xl text-white">{reviewWorkspaceTitle}</DialogTitle>
                  <DialogDescription className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                    Grosse Review-Arbeitsflaeche mit kontrollierter Breite fuer Frage, Antworten, Hinweise und Freigabe.
                  </DialogDescription>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {selectedQuestionDetail ? (
                      <>
                        <Badge className={statusTone[selectedQuestionDetail.status] || statusTone.archived}>
                          {formatStatus(selectedQuestionDetail.status)}
                        </Badge>
                        <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/15">
                          {moduleLabels[selectedQuestionDetail.module_type] || selectedQuestionDetail.module_type}
                        </Badge>
                        <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/15">
                          {scopeLabels[selectedQuestionDetail.scope_type] || selectedQuestionDetail.scope_type}
                        </Badge>
                        <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/15">
                          {detailReviewCount} Review{detailReviewCount === 1 ? "" : "s"}
                        </Badge>
                        {selectedQuestionIndex >= 0 ? (
                          <Badge className="bg-cyan-500/15 text-cyan-100 ring-1 ring-cyan-300/30">
                            {selectedQuestionIndex + 1} / {questions.length}
                          </Badge>
                        ) : null}
                      </>
                    ) : (
                      <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/15">Lade Review-Daten</Badge>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3 xl:min-w-[560px]">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      className="h-11 min-w-[118px] border-white/20 bg-transparent px-4 text-slate-100 hover:bg-white/10"
                      disabled={!hasPreviousQuestion || questionDetailLoading || questionActionBusy !== ""}
                      onClick={() => openAdjacentQuestion(-1)}
                    >
                      Vorherige
                    </Button>
                    <Button
                      variant="outline"
                      className="h-11 min-w-[118px] border-white/20 bg-transparent px-4 text-slate-100 hover:bg-white/10"
                      disabled={!hasNextQuestion || questionDetailLoading || questionActionBusy !== ""}
                      onClick={() => openAdjacentQuestion(1)}
                    >
                      Naechste
                    </Button>
                    <Button
                      className="h-12 min-w-[150px] bg-emerald-400 px-5 text-base font-semibold text-slate-950 hover:bg-emerald-300"
                      disabled={!selectedQuestionDetail || publishDisabled}
                      onClick={() => publishQuestion(selectedQuestionDetail.id)}
                    >
                      {questionActionBusy === `publish:${selectedQuestionDetail?.id}` ? "Publishing..." : "Publish"}
                    </Button>
                    <Button
                      className="h-12 min-w-[150px] border border-rose-300/35 bg-rose-500/18 px-5 text-base font-semibold text-rose-50 hover:bg-rose-500/28"
                      disabled={!selectedQuestionDetail || archiveDisabled}
                      onClick={() => archiveQuestion(selectedQuestionDetail.id)}
                    >
                      {questionActionBusy === `archive:${selectedQuestionDetail?.id}` ? "Archiving..." : "Archive"}
                    </Button>
                    <Button
                      variant="outline"
                      className="h-12 min-w-[120px] border-white/20 bg-transparent px-5 text-base text-slate-100 hover:bg-white/10"
                      onClick={() => setQuestionDetailOpen(false)}
                    >
                      Close
                    </Button>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs leading-5 text-slate-400">
                    {selectedQuestionDetail && !canPublishQuestion(selectedQuestionDetail.status)
                      ? buildQuestionActionHint(selectedQuestionDetail.status)
                      : "Publish bleibt nur fuer fachlich erlaubte Status aktiv. Archive folgt weiterhin den bestehenden Statusregeln."}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 gap-0 xl:grid-cols-[minmax(0,1.45fr)_440px]">
              <div className="min-h-0 overflow-y-auto bg-[linear-gradient(180deg,rgba(15,23,42,0.9),rgba(2,6,23,0.98))] px-6 py-6">
              {questionDetailLoading ? (
                <div className="rounded-3xl border border-white/10 bg-slate-950/60 p-10 text-center text-slate-300">
                  Lade Frage-Details...
                </div>
              ) : null}

              {!questionDetailLoading && questionDetailError ? (
                <div className="rounded-3xl border border-rose-400/25 bg-rose-500/10 p-6 text-sm leading-6 text-rose-100">
                  {questionDetailError}
                </div>
              ) : null}

              {!questionDetailLoading && !questionDetailError && selectedQuestionDetail ? (
                <div className="mx-auto max-w-[1240px] space-y-6 pr-0 xl:pr-6">
                  <section className="rounded-[32px] border border-white/10 bg-slate-950/60 p-7 shadow-[0_24px_80px_rgba(2,6,23,0.35)]">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Fragetext</div>
                        <p className="mt-2 text-sm text-slate-400">Vollstaendige Formulierung fuer den fachlichen Review.</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-slate-400">
                        Frage-ID #{selectedQuestionDetail.id}
                      </div>
                    </div>
                    <p className="mt-6 whitespace-pre-wrap text-[1.45rem] leading-9 text-white">
                      {selectedQuestionDetail.question_text}
                    </p>
                  </section>

                  <section className="rounded-[32px] border border-white/10 bg-slate-950/60 p-7 shadow-[0_20px_60px_rgba(2,6,23,0.28)]">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Antwortoptionen</div>
                        <p className="mt-2 text-sm text-slate-400">Korrekte Antworten sind direkt markiert, falsche Antworten bleiben neutral.</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-slate-400">
                        {detailAnswers.length} Optionen
                      </div>
                    </div>

                    {detailAnswers.length > 0 ? (
                      <div className="mt-6 grid gap-4">
                        {detailAnswers.map((answer) => (
                          <AnswerOptionCard key={answer.id} answer={answer} />
                        ))}
                      </div>
                    ) : (
                      <div className="mt-6 rounded-3xl border border-amber-300/20 bg-amber-500/10 p-5 text-sm leading-6 text-amber-100">
                        Fuer diese Frage wurden keine Antwortoptionen geliefert.
                      </div>
                    )}
                  </section>

                  <section className="rounded-[32px] border border-white/10 bg-slate-950/60 p-7 shadow-[0_20px_60px_rgba(2,6,23,0.28)]">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Interne Erklaerung / Begruendung</div>
                    <p className="mt-3 whitespace-pre-wrap text-base leading-8 text-slate-200">
                      {selectedQuestionDetail.explanation_text || "Keine interne Erklaerung hinterlegt."}
                    </p>
                  </section>
                </div>
              ) : null}
            </div>

              <aside className="min-h-0 min-w-0 overflow-y-auto border-t border-white/10 bg-slate-950/92 px-6 py-6 xl:border-l xl:border-t-0">
              {!questionDetailLoading && !questionDetailError && selectedQuestionDetail ? (
                <div className="space-y-4">
                  <SidebarSection title="Review-Metadaten" subtitle="Status, Scope und Herkunft der Frage kompakt fuer die Freigabeentscheidung.">
                    <div className="grid gap-3">
                      <MetaField label="Status" value={formatStatus(selectedQuestionDetail.status)} />
                      <MetaField label="Beruf" value={selectedQuestionDetail.beruf_label || (selectedQuestionDetail.beruf_id ? `#${selectedQuestionDetail.beruf_id}` : "global")} />
                      <MetaField label="Modul" value={moduleLabels[selectedQuestionDetail.module_type] || selectedQuestionDetail.module_type} />
                      <MetaField label="Scope" value={scopeLabels[selectedQuestionDetail.scope_type] || selectedQuestionDetail.scope_type} />
                      <MetaField label="Kategorie" value={selectedQuestionDetail.category_key || "-"} />
                      <MetaField label="Schwierigkeit" value={selectedQuestionDetail.difficulty || "-"} />
                      <MetaField label="Qualitaetsscore" value={selectedQuestionDetail.quality_score ?? "-"} />
                      <MetaField label="Blueprint" value={selectedQuestionDetail.blueprint_id ? `#${selectedQuestionDetail.blueprint_id} (v${selectedQuestionDetail.blueprint_version || "-"})` : "-"} />
                      <MetaField label="Source Job" value={selectedQuestionDetail.source_job_id ? `#${selectedQuestionDetail.source_job_id}` : "-"} />
                      <MetaField label="Veroeffentlicht am" value={formatDateTime(selectedQuestionDetail.published_at)} />
                    </div>
                  </SidebarSection>

                  <SidebarSection title="Review-Hinweise" subtitle="Gespeicherte Flags und interne Hinweise, ohne den Hauptlesefluss der Frage zu stoeren.">
                    {detailFlags.length > 0 ? (
                      <div className="space-y-2">
                        {detailFlags.map((flag, index) => (
                          <div key={`flag-${index}`} className="rounded-2xl border border-white/10 bg-slate-900/70 p-3 text-sm leading-6 text-slate-200">
                            {formatReviewValue(flag)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm leading-6 text-slate-400">Keine zusaetzlichen Review-Hinweise gespeichert.</p>
                    )}
                  </SidebarSection>

                  <SidebarSection title="Review-Historie" subtitle="Chronologische Review-Eintraege mit Verdict, Stage, Score und Issues.">
                    {selectedQuestionDetail.reviews?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedQuestionDetail.reviews.map((review) => (
                          <div key={review.id} className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/15">{review.review_stage}</Badge>
                              <Badge className={verdictTone[review.verdict] || statusTone.archived}>{formatStatus(review.verdict)}</Badge>
                            </div>
                            <div className="mt-3 grid gap-2 text-xs text-slate-400">
                              <div>Score: {review.score ?? "-"}</div>
                              <div>Issues: {review.issue_count ?? 0}</div>
                              <div>Erfasst: {formatDateTime(review.created_at)}</div>
                            </div>
                            {Array.isArray(review.issues_json) && review.issues_json.length > 0 ? (
                              <div className="mt-3 space-y-2">
                                {review.issues_json.map((issue, index) => (
                                  <div key={`issue-${review.id}-${index}`} className="rounded-xl border border-white/10 bg-slate-950/70 p-3 text-sm leading-6 text-slate-200">
                                    {formatReviewValue(issue)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-3 text-sm leading-6 text-slate-400">Keine Issues fuer diesen Review-Eintrag.</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm leading-6 text-slate-400">Noch keine Review-Eintraege fuer diese Frage vorhanden.</p>
                    )}
                  </SidebarSection>
                </div>
              ) : (
                <div className="rounded-3xl border border-white/10 bg-slate-950/60 p-6 text-sm leading-6 text-slate-400">
                  {questionDetailLoading ? "Die Review-Seitenleiste wird geladen." : "Keine Review-Daten verfuegbar."}
                </div>
              )}
              </aside>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
